import React from "react";
import { FaRedo } from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import AssignmentPanel from "./components/AssignmentPanel";
import AssignedExercisesPanel from "./components/AssignedExercisesPanel";
import ExerciseCatalog from "./components/ExerciseCatalog";
import ExerciseFormCard from "./components/ExerciseFormCard";
import PrepaCoachHero from "./components/PrepaCoachHero";
import PrepaCoachSkeleton from "./components/PrepaCoachSkeleton";
import PrepaStats from "./components/PrepaStats";

import { usePreparationPhysiqueCoachPage } from "./hooks/usePreparationPhysiqueCoachPage";

function PreparationPhysiqueCoach() {
  useScrollToTop();

  const {
    user,
    exercises,
    groups,
    adherentsByGroup,
    allAdherents,
    assignedResults,
    editingExercise,
    selectedExerciseForAssign,
    exerciseForm,
    assignmentForm,
    selectedAdherentIds,
    loading,
    refreshing,
    savingExercise,
    assigning,
    deletingExerciseIds,
    deletingResultKeys,
    stats,
    fetchData,
    handleExerciseInputChange,
    handleSubmitExercise,
    handleEditExercise,
    handleCancelExerciseEdit,
    handleDeleteExerciseWithConfirm,
    handleSelectExerciseForAssign,
    handleAssignmentInputChange,
    handleToggleAdherent,
    handleToggleGroup,
    handleClearSelection,
    handleAssignExercise,
    handleDeleteResultWithConfirm,
  } = usePreparationPhysiqueCoachPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <PrepaCoachHero stats={stats} user={user} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                Préparation physique
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Gestion des exercices
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Crée tes exercices de renforcement, cardio ou mobilité, puis
                attribue-les à un judoka, plusieurs judokas ou un groupe
                complet. Les adhérents pourront ensuite valider les exercices
                réalisés depuis leur espace.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchData}
              disabled={refreshing}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-emerald-300/40 dark:hover:text-emerald-100 lg:min-w-[220px]"
            >
              <FaRedo className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>

          <PrepaStats stats={stats} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {loading ? (
          <PrepaCoachSkeleton />
        ) : (
          <>
            <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <ExerciseCatalog
                exercises={exercises}
                editingExercise={editingExercise}
                selectedExerciseForAssign={selectedExerciseForAssign}
                deletingIds={deletingExerciseIds}
                onEdit={handleEditExercise}
                onDelete={handleDeleteExerciseWithConfirm}
                onAssign={handleSelectExerciseForAssign}
              />

              <div className="space-y-5">
                <ExerciseFormCard
                  form={exerciseForm}
                  editingExercise={editingExercise}
                  saving={savingExercise}
                  onChange={handleExerciseInputChange}
                  onSubmit={handleSubmitExercise}
                  onCancel={handleCancelExerciseEdit}
                />

                <AssignmentPanel
                  groups={groups}
                  adherentsByGroup={adherentsByGroup}
                  allAdherents={allAdherents}
                  selectedExercise={selectedExerciseForAssign}
                  form={assignmentForm}
                  selectedAdherentIds={selectedAdherentIds}
                  assigning={assigning}
                  onChange={handleAssignmentInputChange}
                  onToggleAdherent={handleToggleAdherent}
                  onToggleGroup={handleToggleGroup}
                  onClearSelection={handleClearSelection}
                  onSubmit={handleAssignExercise}
                />
              </div>
            </div>

            <div className="mt-6">
              <AssignedExercisesPanel
                results={assignedResults}
                deletingKeys={deletingResultKeys}
                onDelete={handleDeleteResultWithConfirm}
              />
            </div>
          </>
        )}
      </section>

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default PreparationPhysiqueCoach;